import { ComboboxItem } from "@mantine/core"
import { useQueries } from "@tanstack/react-query"

/**
 * Hook to fetch and merge selected items from an external source (by ID).
 * Ensures selected values are always available in the select options,
 * even if they're not in the current paginated results.
 *
 * Uses one query per ID so results share the React Query cache with other
 * hooks that fetch the same entity (e.g. useBatchedSimpleUser).
 */
export function useSelectedItemFallback<TData>({
  selectedIds,
  entitiesToComboboxData,
  fetchSelectedItem,
  getQueryKey,
}: {
  selectedIds: string | string[] | undefined | null
  entitiesToComboboxData: (data: TData) => ComboboxItem
  fetchSelectedItem?: (id: string) => Promise<TData | null>
  /** Maps a string ID to the query key used to cache that entity — must match the key used by the corresponding dataloader/query hook. */
  getQueryKey: (id: string) => readonly unknown[]
}): {
  selectedComboboxItems: ComboboxItem[]
  isLoadingSelectedItems: boolean
} {
  // Normalize to array
  const idsArray = Array.isArray(selectedIds)
    ? selectedIds
    : selectedIds
      ? [selectedIds]
      : []

  const results = useQueries({
    queries: idsArray.map((id) => ({
      queryKey: getQueryKey(id),
      queryFn: () => fetchSelectedItem!(id),
      enabled: !!fetchSelectedItem,
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: 1,
    })),
  })

  const selectedComboboxItems = results
    .map((r) => r.data)
    .filter((item): item is TData => item !== null && item !== undefined)
    .map(entitiesToComboboxData)

  const isLoadingSelectedItems = results.some((r) => r.isLoading)

  return { selectedComboboxItems, isLoadingSelectedItems }
}
